import { instanceBo } from "../utils/axios.create";

export interface EmailProgramSummary {
	key: string;
	name: string;
	description: string;
	subject: string;
}

/**
 * An email program's template — SIMS-9056. Plain text: the backend escapes
 * every field when it renders, and `{name}` placeholders must be ones the
 * program offers (`EmailProgramConfigView.placeholders`).
 */
export interface EmailProgramTemplate {
	subject: string;
	preheader?: string;
	heading: string;
	/** Paragraphs separated by a blank line. */
	body: string;
	buttonLabel?: string;
	footnote?: string;
}

/** An unsaved template and sender, to preview or test-send. */
export interface EmailProgramDraft {
	template?: EmailProgramTemplate;
	sender?: string;
}

export type EmailProgramConfigSource = "account" | "all-accounts" | "built-in";
export type EmailSenderSource = "account" | "all-accounts" | "account-setting";

/** One program's configuration at one scope (`scopeAccountId: null` = every account). */
export interface EmailProgramConfigView {
	key: string;
	scopeAccountId: string | null;
	saved: {
		template?: EmailProgramTemplate;
		sender?: string;
		updatedBy: string;
		updatedAt?: string;
	} | null;
	effective: {
		template: EmailProgramTemplate;
		templateSource: EmailProgramConfigSource;
		sender?: string;
		senderSource?: EmailSenderSource;
	};
	defaultTemplate: EmailProgramTemplate;
	placeholders: string[];
	verifiedSenderDomains: string[];
}

const rejectWithBody = (err: {
	response?: { data?: unknown };
}): Promise<never> => Promise.reject(err.response?.data ?? err);

const listEmailPrograms = async (): Promise<EmailProgramSummary[]> =>
	instanceBo
		.get("/email-programs")
		.then((res) => res.data.body as EmailProgramSummary[])
		.catch(rejectWithBody);

/**
 * The rendered document, as the chosen account would receive it.
 *
 * `brandingAccountId` is separate from the account already baked into
 * `instanceBo`'s baseURL: that one is the caller's tenant and every backoffice
 * call carries it, while this one is the account being looked AT. Omitted, the
 * backend falls back to the caller's own account.
 *
 * With a `draft`, the unsaved edit is rendered instead of what is saved
 * (POST, because the draft is a body; nothing is stored).
 *
 * Returned as a string rather than as `text/html` on purpose: the caller
 * decides how to display it, and the page puts it in a sandboxed iframe.
 */
const previewEmailProgram = async (
	key: string,
	brandingAccountId?: string,
	draft?: EmailProgramDraft,
): Promise<string> => {
	const request = draft
		? instanceBo.post(`/email-programs/${key}/preview`, {
				...(brandingAccountId ? { brandingAccountId } : {}),
				draft,
			})
		: instanceBo.get(`/email-programs/${key}/preview`, {
				params: brandingAccountId ? { brandingAccountId } : undefined,
			});
	return request
		.then((res) => (res.data.body as { html: string }).html)
		.catch(rejectWithBody);
};

const sendTestEmail = async (
	key: string,
	email: string,
	brandingAccountId?: string,
	draft?: EmailProgramDraft,
): Promise<void> =>
	instanceBo
		.post(`/email-programs/${key}/test-send`, {
			email,
			// Left out entirely when absent, so the request looks the same as it
			// did before this option existed and the backend takes its default.
			...(brandingAccountId ? { brandingAccountId } : {}),
			...(draft ? { draft } : {}),
		})
		.then(() => undefined)
		.catch(rejectWithBody);

const scopeParams = (
	scopeAccountId?: string,
): { scopeAccountId: string } | undefined =>
	scopeAccountId ? { scopeAccountId } : undefined;

const getEmailProgramConfig = async (
	key: string,
	scopeAccountId?: string,
): Promise<EmailProgramConfigView> =>
	instanceBo
		.get(`/email-programs/${key}/config`, {
			params: scopeParams(scopeAccountId),
		})
		.then((res) => res.data.body as EmailProgramConfigView)
		.catch(rejectWithBody);

/**
 * Save one scope's template and sender. The whole record is sent: an omitted
 * template or sender is removed from this scope and inherited again.
 */
const saveEmailProgramConfig = async (
	key: string,
	scopeAccountId: string | undefined,
	input: EmailProgramDraft,
): Promise<EmailProgramConfigView> =>
	instanceBo
		.put(`/email-programs/${key}/config`, {
			...scopeParams(scopeAccountId),
			...(input.template ? { template: input.template } : {}),
			...(input.sender?.trim() ? { sender: input.sender.trim() } : {}),
		})
		.then((res) => res.data.body as EmailProgramConfigView)
		.catch(rejectWithBody);

const resetEmailProgramConfig = async (
	key: string,
	scopeAccountId?: string,
): Promise<EmailProgramConfigView> =>
	instanceBo
		.delete(`/email-programs/${key}/config`, {
			params: scopeParams(scopeAccountId),
		})
		.then((res) => res.data.body as EmailProgramConfigView)
		.catch(rejectWithBody);

export const emailProgramService = {
	listEmailPrograms,
	previewEmailProgram,
	sendTestEmail,
	getEmailProgramConfig,
	saveEmailProgramConfig,
	resetEmailProgramConfig,
};
